import { initializeSkyFireFlow, skyFireFlowContact } from './110-sky-fire-flow.mjs';
import { SKY_WORLD, SKY_WORLD_ASSETS, skyBossWorldScale } from './86-sky-world-scale.mjs';
/** Close encounter rhythm in active seconds. Phase one starts with a shot
 * window; later phases start with a full charge. Finite attacks then give way
 * to a cooling window.
 * Shared by collision apertures and attack dispatch; no presentation clock owns
 * damage. Invalid phase/time resolve to phase one at its opening. */
export const SKY_CLOSE_ENCOUNTER = Object.freeze({
  openingSeconds: 2.4,
  chargeSeconds: 1.35,
  minimumFlightSeconds: 0.8,
  durations: Object.freeze([8.8, 8.2, 7.6]),
});
/** First-person handoff timing in active seconds. Preparation holds the combat
 * clock until physical approach and eye-view setup finish; it grants no damage
 * immunity. The first opening gets extra deliberate aiming time on touch screens.
 * Clearances/shoulder offset are physical metres: the independent crest limit
 * protects the gun ray while the lower lip can approach the actual support hand.
 * The quarter-metre lateral seat moves the pilot, never the eye or hit socket. */
export const SKY_DRAGON_AIM = Object.freeze({
  preparation: 0.4,
  initialDwell: 1.1,
  mouthClearance: 1.35,
  settledMouthClearance: 0.5,
  shoulderApproachOffset: -0.25,
  settledCrestClearance: 0.86,
});

/** Advance the original dragon's input mode. Returns whether its combat clock
 * may advance this tick; movement and articulation always continue. Renderer
 * completion signals cannot expose a target or shorten the preparation. */
export function skyDragonAimControl(boss, dt) {
  if (boss.artVariant !== 'first-dragon') return true;
  if (boss.age < 4.5) {
    boss.controlMode = 'approach';
    boss.aimWindow = false;
    boss.aimPreparing = false;
    return true;
  }
  const beat = skyEncounterBeat(boss.combatPhase, boss.phaseAge);
  const opportunity = beat.opening > 0 && !boss.breath?.active && boss.age >= 4.5;
  if (!opportunity) {
    boss.controlMode = 'steer';
    boss.aimPreparation = 0;
    boss.aimPreparing = false;
    boss.aimWindow = false;
    return true;
  }
  if (boss.controlMode !== 'aim') {
    boss.aimPreparing = true;
    boss.aimPreparation = 0;
    boss.aimWindow = false;
    if (boss.closeHead > 0.45) {
      boss.controlMode = 'aim';
      boss.aimDwell = beat.local < 0.05 ? SKY_DRAGON_AIM.initialDwell : 0;
    } else return !beat.vulnerable;
  }
  boss.aimPreparation = Math.min(1, (boss.aimPreparation ?? 0) + dt / SKY_DRAGON_AIM.preparation);
  boss.aimPreparing = boss.aimPreparation < 1;
  if (boss.aimPreparing) return !beat.vulnerable;
  if (boss.aimDwell > 0) {
    boss.aimDwell = Math.max(0, boss.aimDwell - dt);
    return false;
  }
  return true;
}
/** Protected travel timing in active seconds. The preparation hold lets the
 * camera establish the route before motion; live combat clocks are unaffected. */
export const SKY_TRAVERSAL_TIMING = Object.freeze({
  prepare: 0.4,
  travelScale: 1.2,
  entryBlend: 0.9,
  returnDuration: 0.8,
  reducedDuration: 0.35,
});
const smooth = (v) => {
  const t = Math.max(0, Math.min(1, v));
  return t * t * (3 - 2 * t);
};
export function skyEncounterBeat(phase, activeTime) {
  const p = Math.max(1, Math.min(3, Math.trunc(phase) || 1));
  const age = Math.max(0, Number.isFinite(activeTime) ? activeTime : 0);
  const duration = SKY_CLOSE_ENCOUNTER.durations[p - 1],
    local = Math.round((age % duration) * 1e10) / 1e10;
  const ready = p === 1 ? SKY_CLOSE_ENCOUNTER.openingSeconds : 0;
  const fire = ready + SKY_CLOSE_ENCOUNTER.chargeSeconds,
    secondVolley = fire + 0.4,
    strikeEnd = fire + 0.7;
  const charging = local >= ready && local < fire;
  const striking = local >= fire && local < strikeEnd;
  const opening =
    local < ready
      ? 1 - smooth((local - (ready - 0.25)) / 0.25)
      : local < strikeEnd
        ? 0
        : smooth((local - strikeEnd) / 0.3);
  return {
    duration,
    local,
    firstVolley: fire,
    secondVolley,
    strikeEnd,
    cycle: Math.floor(age / duration),
    opening,
    vulnerable: opening >= 0.999,
    telegraph: charging,
    attackPhase: charging
      ? 'charge'
      : striking
        ? 'strike'
        : local < strikeEnd + 0.6 && local >= strikeEnd
          ? 'recover'
          : 'sweep',
    charge: charging
      ? (local - ready) / (fire - ready)
      : striking
        ? 1
        : Math.max(0, 1 - (local - strikeEnd) / 0.6) * Number(local >= strikeEnd),
    ordinal: [fire, secondVolley].reduce(
      (count, cue) => count + Math.max(0, Math.floor((age - cue + 1e-10) / duration) + 1),
      0,
    ),
  };
}

/** Physical lateral pursuit in metres. The boss follows the pilot's lane with
 * finite damping; its independent close-pass motion remains an aiming problem.
 * The protected entry keeps its original world-space dodge lane. The C2 blend
 * prevents a camera-oriented convenience from teleporting collision geometry. */
export function skyGuardianPursuit(previous, playerX, age, dt, artVariant) {
  const old = Number.isFinite(previous) ? previous : 0;
  // Central crest can accompany more closely. Wide paired intakes need30%
  // relative lateral travel so steering can actually intercept either socket.
  const fraction = artVariant === 'first-dragon' ? 0.95 : 0.7;
  const target = Math.max(-12, Math.min(12, Number.isFinite(playerX) ? playerX : 0)) * fraction;
  const step = Number.isFinite(dt) && dt > 0 ? dt : 0;
  const anchor = old + (target - old) * (1 - Math.exp(-8 * step));
  const u = Math.max(0, Math.min(1, (age - 4.5) / 2));
  const blend = u ** 3 * (10 - 15 * u + 6 * u * u);
  const derivative = u > 0 && u < 1 ? 15 * u * u * (1 - u) ** 2 : 0;
  return {
    anchor,
    x: anchor * blend,
    vx: (step ? (anchor - old) / step : 0) * blend + anchor * derivative,
  };
}

/** Vertical accompaniment covers70% of pilot altitude change, leaving an actual
 * aim correction instead of locking a weakpoint onto the cannon. Ground safety
 * is applied by the caller after combining this offset with the authored lane. */
export function skyGuardianVerticalPursuit(previous, aimY, age, dt) {
  return skyGuardianPursuit(previous, (Number.isFinite(aimY) ? aimY : 4.4) - 4.4, age, dt);
}

// Integrating a smoothstep derivative gives an identity-to-constant C2 join.
// Unlike a hard clamp it preserves honest velocity and continuous acceleration.
function upperLane(value, velocity, ceiling, width) {
  const start = ceiling - width / 2,
    t = (value - start) / width;
  if (t <= 0) return [value, velocity];
  if (t >= 1) return [ceiling, 0];
  return [start + width * (t - t ** 3 + t ** 4 / 2), velocity * (1 - 3 * t * t + 2 * t ** 3)];
}
/** Physical combat root envelope in metres. Close pursuit must leave
 * enough lateral/vertical room for the articulated crest to be intercepted by
 * bounded pilot input. The actual mesh, mouth and collision all use this root;
 * camera offsets never make an unreachable target appear reachable. */
export function skyGuardianCombatLane(pose) {
  if (pose.age <= 4.5) return { x: pose.x, y: pose.y, vx: pose.vx, vy: pose.vy };
  const sign = Math.sign(pose.x) || 1;
  const [x, vx] =
    pose.artVariant === 'first-dragon'
      ? upperLane(Math.abs(pose.x), pose.vx * sign, 9.4, 1.6)
      : [Math.abs(pose.x), pose.vx * sign];
  const original = pose.artVariant === 'first-dragon';
  const floor = original
    ? skyBossWorldScale(pose.profile, pose.design, pose.artVariant).minimumRootY
    : 0;
  const [y, vy] = upperLane(pose.y, pose.vy, original ? floor + 0.1 : 7.8, original ? 0.2 : 0.8);
  return { x: x * sign, vx: vx * sign, y, vy };
}
/** Immutable launch metadata for an actual guardian projectile. Flight is in
 * seconds to the pilot plane; visual cores and contact use the same radius. */
export function skyGuardianThreat(boss, origin, flight) {
  return {
    vz: (SKY_WORLD.pilotZ - origin.z) / flight,
    source: 'guardian',
    sourceId: boss.id,
    attackKind: boss.attackKind,
    radius: boss.attackKind === 'fireball' ? 0.22 : boss.attackKind === 'flame' ? 0.16 : 0.12,
    origin: { x: origin.x, y: origin.y, z: origin.z },
    age: 0,
  };
}

/** Canonical attack appearance for a guardian and its authored pattern. Organic
 * dragon profiles emit mouth fire; other families use energy projectiles. An
 * unknown family safely retains the ordinary energy appearance. No units. */
export function skyGuardianAttackKind(boss, pattern) {
  if (boss.artVariant === 'first-dragon') return 'flame';
  const dragon = ['serpent', 'twin', 'skeletalSerpent', 'skeletalTwin'].includes(boss.profile);
  return dragon ? (pattern === 'fan' ? 'flame' : 'fireball') : 'energy';
}

/** Capture a protected route in metres using the actual authored boss envelope.
 * Position Y is the pilot aim reference, not the sole. Distinct routes are short
 * once-per-damage-phase beats; only ordinary combat can deal or receive damage. */
export function skyCreateTraversal(state, phase, dimensions, reduced = false) {
  const boss = state.boss,
    sign = Math.abs(boss.x) > 1 ? -Math.sign(boss.x) : phase === 2 ? -1 : 1;
  const origin = { x: state.x, y: state.y, z: SKY_WORLD.pilotZ };
  const center = { x: boss.x, y: boss.y, z: boss.z };
  const side = dimensions.width / 2 + 2;
  const front = dimensions.length / 2 + 2;
  const high = center.y + dimensions.height + 2;
  const low = 2.3;
  const giant = boss.artVariant === 'first-dragon';
  const kind = ['overhead', 'flank', 'underwing'][phase - 1];
  const point = (x, y, z) => ({ x: center.x + sign * x, y, z: center.z + z });
  const points =
    phase === 1
      ? [
          origin,
          point(side * 0.6, high, front * 0.4),
          point(0, high, -front),
          point(-side, high, front * 0.2),
          origin,
        ]
      : phase === 2
        ? [
            origin,
            point(side, center.y + 1, front * 0.4),
            point(side, center.y + 1.5, -front),
            point(-side, high, -front * 0.4),
            origin,
          ]
        : [
            origin,
            point(side + 1.5, low + 0.6, front * 0.45),
            point(dimensions.width * 0.38, low, -front * 0.35),
            point((giant ? -1 : 1) * (side + 1.5), low + 1, -front),
            origin,
          ];
  const duration = reduced
    ? SKY_TRAVERSAL_TIMING.reducedDuration
    : SKY_TRAVERSAL_TIMING.prepare +
      SKY_TRAVERSAL_TIMING.travelScale * (giant ? 5 : phase === 1 ? 4.8 : 4.2);
  return {
    id: `${boss.id}:${phase}`,
    phase: kind,
    combatPhase: phase,
    age: 0,
    duration,
    prepareDuration: reduced ? 0 : SKY_TRAVERSAL_TIMING.prepare,
    reduced,
    origin,
    originLocomotion: state.locomotion,
    center,
    points,
    // Preserve launch geometry when Skip replaces the live return bridge.
    // Presentation can frame that route without steering the pilot or creature.
    pathPoints: Object.freeze(points.map((point) => Object.freeze({ ...point }))),
    pathDuration: duration,
    position: { ...origin },
    tangent: { x: 0, y: 0, z: -1 },
    bossFocus: { ...center },
    beat: reduced ? kind : 'prepare',
    control: 'scripted',
    protected: true,
  };
}

function routeCurve(points, progress) {
  const p = Math.max(0, Math.min(1, progress)) * (points.length - 1);
  const index = Math.min(points.length - 2, Math.floor(p)),
    u = p - index;
  const at = (i) => points[Math.max(0, Math.min(points.length - 1, i))];
  const a = at(index - 1),
    b = at(index),
    c = at(index + 1),
    d = at(index + 2);
  const position = {},
    velocity = {};
  for (const key of ['x', 'y', 'z']) {
    const v0 = index === 0 ? 0 : (c[key] - a[key]) / 2;
    const v1 = index === points.length - 2 ? 0 : (d[key] - b[key]) / 2;
    const a0 = index === 0 ? 0 : c[key] - 2 * b[key] + a[key];
    const a1 = index === points.length - 2 ? 0 : d[key] - 2 * c[key] + b[key];
    const p0 = b[key],
      p1 = v0,
      p2 = a0 / 2;
    const delta = c[key] - p0 - p1 - p2,
      speed = v1 - p1 - 2 * p2,
      acceleration = a1 - 2 * p2;
    const p3 = 10 * delta - 4 * speed + acceleration / 2;
    const p4 = -15 * delta + 7 * speed - acceleration;
    const p5 = 6 * delta - 3 * speed + acceleration / 2;
    position[key] = p0 + p1 * u + p2 * u ** 2 + p3 * u ** 3 + p4 * u ** 4 + p5 * u ** 5;
    velocity[key] = p1 + 2 * p2 * u + 3 * p3 * u ** 2 + 4 * p4 * u ** 3 + 5 * p5 * u ** 4;
  }
  const length = Math.hypot(velocity.x, velocity.y, velocity.z);
  return {
    position,
    tangent:
      length > 0.00001
        ? Object.fromEntries(Object.entries(velocity).map(([key, value]) => [key, value / length]))
        : { x: 0, y: 0, z: -1 },
  };
}
/** Deterministic C2 path sample. A skipped pass returns from its current actual
 * coordinate over a short protected bridge rather than teleporting the pilot.
 * A reduced-motion pass requested before entry never leaves the combat point. */
export function skyTraversalFrame(route) {
  const prepare = route.returning || route.reduced ? 0 : (route.prepareDuration ?? 0);
  const progress = Math.max(0, Math.min(1, (route.age - prepare) / (route.duration - prepare)));
  const sampled = route.reduced
    ? { position: { ...route.origin }, tangent: { x: 0, y: 0, z: -1 } }
    : routeCurve(route.points, progress);
  const lift = route.returning
    ? (route.returnLift ?? 0) * (1 - smooth(progress))
    : route.phase === 'underwing' && !route.reduced
      ? 4 * Math.sin(Math.PI * progress) ** 2
      : 0;
  const cameraWeight = route.returning
    ? (route.returnCameraWeight ?? 1) * (1 - smooth(progress))
    : smooth((route.age - prepare) / SKY_TRAVERSAL_TIMING.entryBlend) *
      (1 - smooth((progress - 0.7) / 0.3));
  return {
    ...sampled,
    bossLift: lift,
    cameraWeight,
    progress,
    beat:
      route.returning || progress > 0.76 ? 'return' : route.age < prepare ? 'prepare' : route.phase,
    bossFocus: { ...route.center, y: route.center.y + lift },
  };
}

/** Ignition locks the telegraphed world direction. Emitted material then coasts
 * independently of the moving mouth; calibrated pressure preserves at least
 *0.8s before the hot front reaches the original target plane. Units are metres. */
export function skyCreateBreath(boss, target, sourceVelocity) {
  const origin = { ...boss.attackSocket };
  const delta = { x: target.x - origin.x, y: target.y - origin.y, z: SKY_WORLD.pilotZ - origin.z };
  const distance = Math.hypot(delta.x, delta.y, delta.z);
  if (!(distance > 0.1) || delta.z <= 0) return null;
  return initializeSkyFireFlow(
    {
      origin,
      target: { x: target.x, y: target.y, z: SKY_WORLD.pilotZ },
      direction: Object.fromEntries(
        Object.entries(delta).map(([key, value]) => [key, value / distance]),
      ),
      range: distance + 3,
      length: 0,
      speed: distance / SKY_CLOSE_ENCOUNTER.minimumFlightSeconds,
      reactionSeconds: SKY_CLOSE_ENCOUNTER.minimumFlightSeconds,
      nearRadius: 0.18,
      farRadius: [0, 0.85, 1.1, 1.35][boss.combatPhase] ?? 0.85,
      age: 0,
      duration: 1.35,
      active: true,
      attackId: `${boss.id}:${boss.combatPhase}:${boss.phaseAge}`,
      launchOrigin: { ...origin },
    },
    sourceVelocity,
  );
}
/** Actual finite jet contact; invalid or inactive volumes never deal damage. */
export function skyBreathContact(breath, point, radius = SKY_WORLD.pilotContactRadius) {
  return skyFireFlowContact(breath, point, radius);
}

/** Physical head-side shooting windows for the original dragon. The root
 * advances while the crest is exposed, withdraws before ignition, then returns
 * for the cooling opportunity. Position and velocity survive every handoff;
 * neither the camera nor model scale supplies this approach. Units are metres. */
export function skyGuardianFacingDepth(pose, previousZ, previousVZ, dt) {
  if (pose.artVariant !== 'first-dragon' || pose.age < 4.5)
    return { z: pose.z, vz: pose.vz, closeHead: 0 };
  const beat = skyEncounterBeat(pose.combatPhase, pose.phaseAge);
  const exposure = pose.breath?.active ? 0 : beat.opening;
  // The measured mouth follows the same scaled/animated skeleton as contact.
  // Keeping its lip ahead of the cannon preserves clearance when art size changes.
  const mouthOffset = Number.isFinite(pose.depthMouthOffset)
    ? pose.depthMouthOffset
    : 3.65 * SKY_WORLD_ASSETS.dragon.modelScale;
  const near =
    Number.isFinite(pose.depthCrestOffset) && pose.aimApproached
      ? Math.min(
          -SKY_DRAGON_AIM.settledMouthClearance - mouthOffset,
          -SKY_DRAGON_AIM.settledCrestClearance - pose.depthCrestOffset,
        )
      : -SKY_DRAGON_AIM.mouthClearance - mouthOffset;
  const target = near - 5 * (1 - exposure);
  const old = Number.isFinite(previousZ) ? previousZ : pose.z;
  const speed = Number.isFinite(previousVZ) ? previousVZ : pose.vz;
  const step = Number.isFinite(dt) && dt > 0 ? Math.min(dt, 2) : 0;
  const omega = 4,
    decay = Math.exp(-omega * step),
    delta = old - target;
  const movement = (speed + omega * delta) * step;
  const z = target + (delta + movement) * decay;
  const vz = (speed - omega * movement) * decay;
  // Actual proximity fades the close camera as the creature physically leaves;
  // a phase boundary never abruptly changes the viewpoint.
  return { z, vz, closeHead: smooth((z - near + 3) / 2.8) };
}
